import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlineBell } from "react-icons/hi2";
import { notificationService } from "../services";
import "./NotificationDropdown.css";

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationService.getUnreadCount();
      // data expected to be { count: number }
      setUnreadCount(data?.count || 0);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, []);

  const fetchLatestNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationService.getNotifications({ page: 1, pageSize: 10 });
      setNotifications(data?.items || []);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch of both count and items
    fetchUnreadCount();
    fetchLatestNotifications();
    
    // Background polling every 30s so the data is always fresh when opened
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchLatestNotifications();
    }, 30000); 
    
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchLatestNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEsc = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const handleNotifClick = async (n) => {
    try {
      if (!n.isRead) {
        await notificationService.markAsRead(n.notificationId);
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setIsOpen(false);
      if (n.actionUrl) {
        navigate(n.actionUrl);
      }
    } catch (err) {
      console.error("Failed to process notification click:", err);
    }
  };

  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'action_required': return '🔴';
      default: return 'ℹ️';
    }
  };

  return (
    <div className="notif-dropdown" ref={dropdownRef}>
      <button 
        className="notif-bell" 
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <HiOutlineBell />
        {unreadCount > 0 && (
          <span className="notif-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notif-panel">
          <div className="notif-panel-header">
            <h4>Notifications</h4>
            {unreadCount > 0 && (
              <button 
                className="notif-mark-all" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAllRead();
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notif-panel-list">
            {loading ? (
              <div className="notif-loading">Loading alerts…</div>
            ) : notifications.length === 0 ? (
              <div className="notif-empty">
                <HiOutlineBell className="notif-empty-icon" />
                <p>No new notifications</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.notificationId}
                  className={`notif-item ${n.isRead ? '' : 'notif-item--unread'}`}
                  onClick={() => handleNotifClick(n)}
                >
                  <span className="notif-type-icon">
                    {getTypeIcon(n.type)}
                  </span>
                  <div className="notif-item-body">
                    <span className="notif-item-title">{n.title}</span>
                    <span className="notif-item-time">{timeAgo(n.createdAt)}</span>
                  </div>
                  {!n.isRead && <span className="notif-unread-dot" />}
                </button>
              ))
            )}
          </div>

          <Link 
            to="/home/notifications" 
            className="notif-panel-footer" 
            onClick={() => setIsOpen(false)}
          >
            See all notifications
          </Link>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
